import { loadFleetData } from './api/fleet-data';
import { clearConfig } from './api/geotab-client';
import { state } from './state';
import { displayVehicles } from './ui/bubble-grid';
import { initFaultModal } from './ui/fault-modal';
import { initFilters, initFieldSelector, resetFilters } from './ui/filters';
import { updateLastRefreshDisplay, setDatabaseLabel } from './ui/topbar';
import { initIdlingPage, resetIdlingPage, refreshIdlingCharts } from './idling/idling-page';
import { initSafetyPage, resetSafetyPage } from './safety/safety-page';
import { initHeatmapPage, resetHeatmapPage } from './heatmap/heatmap-page';
import { initAssetsPage, resetAssetsPage } from './assets/assets-page';
import { initLoginAuditPage, resetLoginAuditPage } from './login-audit/login-audit-page';
import { initWatchdogPage, resetWatchdogPage, startWatchdogRefresh, stopWatchdogRefresh } from './watchdog/watchdog-page';
import {
  initZoneActivityPage, resetZoneActivityPage,
  startZoneActivityRefresh, stopZoneActivityRefresh,
} from './zone-activity/zone-activity-page';
import { initResetDevicePage, resetResetDevicePage } from './reset-device/reset-device-page';
import { RESET_DEVICE_ENABLED } from './reset-device/reset-device-config';

// ── Dashboard bootstrap ───────────────────────────────────────────────────

let wired = false;

export function initDashboard(database: string): void {
  if (database) {
    state.currentDatabase = database;
    setDatabaseLabel(database);
  }

  // Wire dashboard listeners exactly once. initDashboard runs on every login, but the dashboard
  // DOM is static (logout only toggles screen visibility), so re-running these would stack a
  // second set of listeners on the same elements. The filter chips and field-selector dropdown
  // toggle state, so an even number of duplicate handlers cancels out — leaving the filters
  // unresponsive after switching databases until another logout/login made the count odd again.
  if (!wired) {
    initSidebar();
    initFilters();
    initFieldSelector();
    initFaultModal();
    initHelpModal();
    initExportButton();
    initRefreshTimer();
    wired = true;
  }

  loadAndDisplay();
}

// ── Data load + display ───────────────────────────────────────────────────

// On large fleets a full fetch + compose + render can take much longer than the refresh
// interval. Without this guard, setInterval stacks concurrent loads, each holding tens of MB
// of fault/status data, until the tab exhausts memory and the whole machine starts swapping
// (the multi-minute freeze + system-wide mouse lag reported on 3000+ vehicle databases).
let loadInFlight = false;

// Auto-refresh cadence scales with fleet size: reloading thousands of vehicles every 10s is
// far too heavy, and the underlying data (30-day faults, status snapshots) barely changes
// that fast. Small fleets keep the snappy 10s cadence.
function refreshIntervalMs(): number {
  const n = state.allVehicles.length;
  if (n > 2000) return 60000;
  if (n > 500)  return 30000;
  return 10000;
}

async function loadAndDisplay(silent = false): Promise<void> {
  if (loadInFlight) return;
  loadInFlight = true;

  // Snapshot the session epoch. If handleLogout() bumps it before this fetch resolves (user
  // logged out, or logged into a different database, while this call was in flight), the
  // response below is stale and must be discarded rather than applied to the new session.
  const epoch = state.sessionEpoch;

  const loadingContainer = document.getElementById('loadingContainer') as HTMLElement;
  const vehicleGrid      = document.getElementById('vehicleGrid')      as HTMLElement;

  if (!silent) {
    loadingContainer.classList.remove('hidden');
    vehicleGrid.classList.add('hidden');
  }

  try {
    const vehicles = await loadFleetData();
    if (epoch !== state.sessionEpoch) return; // stale session — discard

    state.allVehicles = vehicles;
    state.dataVersion++;
    displayVehicles(state.allVehicles);

    if (!silent) {
      loadingContainer.classList.add('hidden');
      vehicleGrid.classList.remove('hidden');
    }

    state.lastRefreshTime = new Date();
    updateLastRefreshDisplay();

    if (!state.refreshInterval) {
      state.refreshInterval = setInterval(() => {
        state.silentRefreshing = true;
        loadAndDisplay(true).finally(() => { state.silentRefreshing = false; });
      }, refreshIntervalMs());
    }
  } catch (error) {
    if (epoch !== state.sessionEpoch) return; // stale session — ignore the error too
    console.error('Error loading fleet data:', error);
    const label = loadingContainer.querySelector<HTMLElement>('.loading-label');
    if (label) label.textContent = 'Error: ' + (error as Error).message;
  } finally {
    // Only release the guard on behalf of the session that still owns it. A stale call
    // resolving after a relogin must not clear the flag out from under the new session's
    // own in-flight load.
    if (epoch === state.sessionEpoch) loadInFlight = false;
  }
}

// ── Sidebar ───────────────────────────────────────────────────────────────

function initSidebar(): void {
  const shell  = document.getElementById('dashboardScreen')!;
  const toggle = document.getElementById('sidebarToggle')!;

  if (localStorage.getItem('sbCollapsed') === '1') shell.classList.add('sb-collapsed');

  toggle.addEventListener('click', () => {
    shell.classList.toggle('sb-collapsed');
    localStorage.setItem('sbCollapsed', shell.classList.contains('sb-collapsed') ? '1' : '0');
  });

  document.getElementById('navHealth')! .addEventListener('click', () => switchPage('health'));
  document.getElementById('navIdling')! .addEventListener('click', () => switchPage('idling'));
  document.getElementById('navSafety')! .addEventListener('click', () => switchPage('safety'));
  document.getElementById('navHeatmap')!.addEventListener('click', () => switchPage('heatmap'));
  document.getElementById('navAssets')!     .addEventListener('click', () => switchPage('assets'));
  document.getElementById('navLoginAudit')! .addEventListener('click', () => switchPage('loginAudit'));
  document.getElementById('navZoneActivity')!.addEventListener('click', () => switchPage('zoneActivity'));
  document.getElementById('navWatchdog')!   .addEventListener('click', () => switchPage('watchdog'));

  // Reset Device ships behind a kill switch: with it off the nav item is hidden
  // and switchPage never routes there, leaving every other page untouched.
  const navResetDevice = document.getElementById('navResetDevice')!;
  if (RESET_DEVICE_ENABLED) navResetDevice.addEventListener('click', () => switchPage('resetDevice'));
  else                      navResetDevice.classList.add('hidden');

  document.getElementById('logoutBtn')!.addEventListener('click', handleLogout);
  document.getElementById('refreshBtn')!.addEventListener('click', () => loadAndDisplay());
}

function switchPage(page: 'health' | 'idling' | 'safety' | 'heatmap' | 'assets' | 'loginAudit' | 'zoneActivity' | 'watchdog' | 'resetDevice'): void {
  if (page === 'resetDevice' && !RESET_DEVICE_ENABLED) return;
  document.getElementById('healthPage')!     .classList.toggle('hidden', page !== 'health');
  document.getElementById('idlingPage')!     .classList.toggle('hidden', page !== 'idling');
  document.getElementById('safetyPage')!     .classList.toggle('hidden', page !== 'safety');
  document.getElementById('heatmapPage')!    .classList.toggle('hidden', page !== 'heatmap');
  document.getElementById('assetsPage')!     .classList.toggle('hidden', page !== 'assets');
  document.getElementById('loginAuditPage')! .classList.toggle('hidden', page !== 'loginAudit');
  document.getElementById('zoneActivityPage')!.classList.toggle('hidden', page !== 'zoneActivity');
  document.getElementById('watchdogPage')!   .classList.toggle('hidden', page !== 'watchdog');
  document.getElementById('resetDevicePage')!.classList.toggle('hidden', page !== 'resetDevice');
  document.getElementById('navHealth')!     .classList.toggle('active', page === 'health');
  document.getElementById('navIdling')!     .classList.toggle('active', page === 'idling');
  document.getElementById('navSafety')!     .classList.toggle('active', page === 'safety');
  document.getElementById('navHeatmap')!    .classList.toggle('active', page === 'heatmap');
  document.getElementById('navAssets')!     .classList.toggle('active', page === 'assets');
  document.getElementById('navLoginAudit')! .classList.toggle('active', page === 'loginAudit');
  document.getElementById('navZoneActivity')!.classList.toggle('active', page === 'zoneActivity');
  document.getElementById('navWatchdog')!   .classList.toggle('active', page === 'watchdog');
  document.getElementById('navResetDevice')!.classList.toggle('active', page === 'resetDevice');
  if (page === 'idling')      { initIdlingPage(); requestAnimationFrame(refreshIdlingCharts); }
  if (page === 'safety')      initSafetyPage();
  if (page === 'heatmap')     initHeatmapPage();
  if (page === 'assets')      initAssetsPage();
  if (page === 'loginAudit')  initLoginAuditPage();
  if (page === 'resetDevice') initResetDevicePage();
  // Watchdog runs a 30s connectivity refresh loop while visible; stop it whenever we leave.
  if (page === 'watchdog') { initWatchdogPage(); startWatchdogRefresh(); }
  else                       stopWatchdogRefresh();
  // Zone Activity polls live positions on its own loop; same start/stop discipline.
  if (page === 'zoneActivity') { initZoneActivityPage(); startZoneActivityRefresh(); }
  else                          stopZoneActivityRefresh();
}

// ── Logout ────────────────────────────────────────────────────────────────

function handleLogout(): void {
  // Invalidate any in-flight loadAndDisplay() call and free the guard immediately so the next
  // login isn't blocked waiting on a fetch that belongs to the session being torn down.
  state.sessionEpoch++;
  loadInFlight = false;

  if (state.refreshInterval) {
    clearInterval(state.refreshInterval);
    state.refreshInterval = null;
  }
  state.allVehicles             = [];
  state.currentFilteredVehicles = [];
  resetFilters();
  clearConfig();

  resetIdlingPage();
  resetSafetyPage();
  resetHeatmapPage();
  resetAssetsPage();
  resetLoginAuditPage();
  resetWatchdogPage();
  resetZoneActivityPage();
  resetResetDevicePage();

  document.getElementById('dashboardScreen')!.classList.add('hidden');
  document.getElementById('loginScreen')!.classList.remove('hidden');
  (document.getElementById('loginForm') as HTMLFormElement).reset();
  const loginBtn = document.getElementById('loginBtn') as HTMLButtonElement;
  loginBtn.disabled    = false;
  loginBtn.textContent = 'Connect to Fleet';
  document.getElementById('loginError')!.classList.remove('show');
}

// ── Download Report (CSV export) ──────────────────────────────────────────

function initExportButton(): void {
  document.getElementById('exportCsvBtn')!.addEventListener('click', async () => {
    const { exportVehicleHealthXLSX } = await import('./ui/vehicle-health-export');
    exportVehicleHealthXLSX();
  });
}

// ── Help modal ────────────────────────────────────────────────────────────

function initHelpModal(): void {
  document.getElementById('helpBtn')!.addEventListener('click', () =>
    document.getElementById('helpModal')!.classList.add('show')
  );
  document.getElementById('helpModalClose')!.addEventListener('click', () =>
    document.getElementById('helpModal')!.classList.remove('show')
  );
  document.getElementById('helpModal')!.addEventListener('click', (e) => {
    if ((e.target as Element).id === 'helpModal')
      document.getElementById('helpModal')!.classList.remove('show');
  });
}

// ── Refresh timer display ─────────────────────────────────────────────────

function initRefreshTimer(): void {
  setInterval(updateLastRefreshDisplay, 10000);
}

// ── Theme toggle ──────────────────────────────────────────────────────────

const MOON_SVG = `<svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M15.5 10.5A7 7 0 0 1 7.5 2.5a7 7 0 1 0 8 8z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

const SUN_SVG = `<svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
  <circle cx="9" cy="9" r="3.5" stroke="currentColor" stroke-width="1.5"/>
  <path d="M9 1v2M9 15v2M1 9h2M15 9h2M3.22 3.22l1.42 1.42M13.36 13.36l1.42 1.42M3.22 14.78l1.42-1.42M13.36 4.64l1.42-1.42" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
</svg>`;

export function initThemeToggle(): void {
  const saved = localStorage.getItem('fleetTheme') ?? 'dark';
  applyTheme(saved, false);

  const toggle = () => {
    const current = document.documentElement.getAttribute('data-theme') ?? 'dark';
    const next = current === 'dark' ? 'light' : 'dark';
    applyTheme(next, true);
    localStorage.setItem('fleetTheme', next);
  };

  document.getElementById('themeToggleBtn')?.addEventListener('click', toggle);

  document.querySelectorAll('.theme-ph-toggle').forEach(btn => {
    btn.addEventListener('click', toggle);
  });
}

function applyTheme(theme: string, dispatch: boolean): void {
  document.documentElement.setAttribute('data-theme', theme);
  const isLight = theme === 'light';
  const svg = isLight ? MOON_SVG : SUN_SVG;

  const icon = document.getElementById('themeToggleIcon');
  if (icon) icon.innerHTML = svg;

  document.querySelectorAll<HTMLElement>('.theme-ph-icon').forEach(el => {
    el.innerHTML = svg;
  });

  if (dispatch) {
    document.dispatchEvent(new CustomEvent('fleet-theme-change', { detail: { isLight } }));
  }
}
